import React, { useState } from 'react';
import { X, Fuel, Edit, Save, Trash2 } from 'lucide-react';
import api from '../../api.js';
import { useOrgCurrency } from '../../hooks/useOrgCurrency.js';
import { useFormat } from '../../i18n/format.js';

function FuelDetailModal({ log, onClose, onUpdate, onMessage, vehicles, drivers, readOnly = false }) {
  const { t, fmt } = useFormat();
  // Devise de l'organisation du plein — un seul outil monétaire.
  const { money, symbolOf } = useOrgCurrency();
  const symbol = symbolOf(log);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editData, setEditData] = useState({
    fuel_date: log.fuel_date,
    odometer: log.odometer || '',
    liters: log.liters,
    total_cost: log.total_cost,
    fuel_type: log.fuel_type,
    vendor: log.vendor || '',
    card_number: log.card_number || ''
  });

  // Get vehicle info
  const getVehicleInfo = () => {
    const vehicle = vehicles.find(v => v.id === log.vehicle_id);
    return vehicle || null;
  };

  // Get driver info
  const getDriverInfo = () => {
    if (!log.driver_id) return null;
    return drivers.find(d => d.id === log.driver_id) || null;
  };

  // Fuel type configuration — le libellé vient de i18n (fuel.types.<code>)
  const getFuelTypeConfig = (type) => {
    const config = {
      gasoline: { code: 'gasoline', color: 'bg-green-100 text-green-800', icon: '⛽' },
      diesel: { code: 'diesel', color: 'bg-yellow-100 text-yellow-800', icon: '🛢️' },
      electric: { code: 'electric', color: 'bg-blue-100 text-blue-800', icon: '⚡' },
      hybrid: { code: 'hybrid', color: 'bg-purple-100 text-purple-800', icon: '🔋' },
      other: { code: 'other', color: 'bg-gray-100 text-gray-800', icon: '🔧' }
    };
    return config[type] || config.other;
  };

  // Calculate price per liter
  const pricePerLiter = log.liters > 0 ? log.total_cost / log.liters : 0;

  // Handle save
  const handleSave = async () => {
    setLoading(true);
    try {
      await api.fuelLog.update(log.id, {
        ...editData,
        odometer: editData.odometer ? parseInt(editData.odometer) : null,
        liters: parseFloat(editData.liters),
        total_cost: parseFloat(editData.total_cost)
      });

      onMessage?.({
        title: t('fuel.messages.successTitle'),
        message: t('fuel.messages.updated'),
        type: 'success'
      });

      onUpdate?.();
      setIsEditing(false);
    } catch (error) {
      onMessage?.({
        title: t('fuel.messages.errorTitle'),
        message: t('fuel.errors.updateFailed'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!window.confirm(t('fuel.messages.confirmDelete'))) return;

    setLoading(true);
    try {
      await api.fuelLog.delete(log.id);
      onMessage?.({
        title: t('fuel.messages.successTitle'),
        message: t('fuel.messages.deleted'),
        type: 'success'
      });
      onUpdate?.();
      onClose();
    } catch (error) {
      onMessage?.({
        title: t('fuel.messages.errorTitle'),
        message: t('fuel.errors.deleteFailed'),
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const vehicle = getVehicleInfo();
  const driver = getDriverInfo();
  const fuelConfig = getFuelTypeConfig(log.fuel_type);

  if (!log) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-green-500 text-white px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Fuel className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">{t('fuel.detail.title', { id: log.id })}</h2>
              <p className="text-sm text-green-100">{log.fuel_date}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Main Info Card */}
          <div className="bg-green-50 border-2 border-green-200 p-6 rounded-lg mb-6">
            <div className="flex justify-between items-start">
              <div>
                <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-lg ${fuelConfig.color}`}>
                  <span className="text-2xl">{fuelConfig.icon}</span>
                  {t(`fuel.types.${fuelConfig.code}`)}
                </span>
              </div>
              <div className="text-right">
                <div className="text-3xl font-bold text-green-600">
                  {money(log.total_cost, log)}
                </div>
                <div className="text-lg text-gray-600">
                  {t('fuel.detail.litersAtPrice', {
                    liters: fmt.number(log.liters, { decimals: 2 }),
                    price: fmt.number(pricePerLiter, { decimals: 3 }),
                    symbol
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Edit Form or Details */}
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editDate')}</label>
                  <input
                    type="date"
                    value={editData.fuel_date}
                    onChange={(e) => setEditData({ ...editData, fuel_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editOdometer')}</label>
                  <input
                    type="number"
                    value={editData.odometer}
                    onChange={(e) => setEditData({ ...editData, odometer: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editLiters')}</label>
                  <input
                    type="number"
                    value={editData.liters}
                    onChange={(e) => setEditData({ ...editData, liters: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editTotalCost', { symbol })}</label>
                  <input
                    type="number"
                    value={editData.total_cost}
                    onChange={(e) => setEditData({ ...editData, total_cost: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    step="0.01"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editVendor')}</label>
                  <input
                    type="text"
                    value={editData.vendor}
                    onChange={(e) => setEditData({ ...editData, vendor: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('fuel.detail.editCardNumber')}</label>
                  <input
                    type="text"
                    value={editData.card_number}
                    onChange={(e) => setEditData({ ...editData, card_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Vehicle & Driver Info */}
              <div className="grid grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                    {t('fuel.detail.vehicleSection')}
                  </h3>
                  {vehicle ? (
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">{t('fuel.detail.makeModel')}</span> {vehicle.make} {vehicle.model}</p>
                      <p><span className="text-gray-500">{t('fuel.detail.plate')}</span> {vehicle.license_plate}</p>
                      <p><span className="text-gray-500">{t('fuel.detail.currentOdometer')}</span> {vehicle.mileage != null ? t('fuel.detail.kmValue', { value: fmt.number(vehicle.mileage) }) : '-'}</p>
                    </div>
                  ) : (
                    <p className="text-gray-500">{t('fuel.detail.vehicleFallback', { id: log.vehicle_id })}</p>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    {t('fuel.detail.driverSection')}
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-500">{t('fuel.detail.driverLabel')}</span> {driver ? `${driver.first_name} ${driver.last_name}` : '-'}</p>
                    <p><span className="text-gray-500">{t('fuel.detail.stationLabel')}</span> {log.vendor || '-'}</p>
                    <p><span className="text-gray-500">{t('fuel.detail.cardLabel')}</span> {log.card_number || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">{t('fuel.detail.detailsSection')}</h3>
                <div className="grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">{t('fuel.detail.date')}</p>
                    <p className="font-medium text-gray-900">{log.fuel_date}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fuel.detail.odometer')}</p>
                    <p className="font-medium text-gray-900">{log.odometer ? t('fuel.detail.kmValue', { value: fmt.number(log.odometer) }) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fuel.detail.quantity')}</p>
                    <p className="font-medium text-gray-900">{t('fuel.detail.litersValue', { value: fmt.number(log.liters, { decimals: 2 }) })}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">{t('fuel.detail.pricePerLiter')}</p>
                    <p className="font-medium text-gray-900">{t('fuel.detail.pricePerLiterValue', { price: fmt.number(pricePerLiter, { decimals: 3 }), symbol })}</p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Timestamps */}
          <div className="text-sm text-gray-500">
            {log.created_at && <p>{t('fuel.detail.createdAt', { date: fmt.dateTime(log.created_at) })}</p>}
            {log.updated_at && <p>{t('fuel.detail.updatedAt', { date: fmt.dateTime(log.updated_at) })}</p>}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 flex justify-between items-center bg-gray-50">
          {readOnly ? <span /> : (
            <button
              onClick={handleDelete}
              className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg flex items-center gap-2"
              disabled={loading}
            >
              <Trash2 className="w-4 h-4" />
              {t('common.actions.delete')}
            </button>
          )}

          <div className="flex gap-3">
            {isEditing && !readOnly ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  disabled={loading}
                >
                  {t('common.actions.cancel')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {loading ? t('common.states.saving') : t('common.actions.save')}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  {t('common.actions.close')}
                </button>
                {!readOnly && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    {t('common.actions.edit')}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default FuelDetailModal;
